import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useCart } from '../contexts/CartContext';
import ProductCard from '../components/ProductCard';

const Favorites: React.FC = () => {
  const { favorites, removeFromFavorites } = useUser();
  const { addToCart } = useCart();

  const HeartIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">My Favorites</h1>
          <p className="text-neutral-600">Items you've saved for later</p>
        </div>

        {favorites.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-12 text-center animate-fade-in-up">
            <div className="w-20 h-20 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <HeartIcon />
            </div>
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">No favorites yet</h3>
            <p className="text-neutral-600 mb-6">
              Start adding items to your favorites by clicking the heart icon on products
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-white rounded-xl font-semibold hover:bg-accent-700 hover:shadow-xl hover:shadow-accent-500/25 transition-all duration-300"
            >
              Explore Products
            </Link>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-6 mb-8 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-600 mb-1">Total Favorites</p>
                  <p className="text-2xl font-bold text-neutral-900">{favorites.length} items</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-neutral-600 mb-1">Total Value</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    ₹{favorites.reduce((sum, product) => sum + product.price, 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Favorites Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favorites.map((product, index) => (
                <div
                  key={product.id}
                  className="relative animate-fade-in-up"
                  style={{ animationDelay: `${(index + 2) * 100}ms` }}
                >
                  {/* Remove from favorites button */}
                  <button
                    onClick={() => removeFromFavorites(product.id)}
                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-red-500 hover:text-red-600 hover:scale-110 transition-all duration-200"
                    title="Remove from favorites"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  </button>
                  
                  <ProductCard
                    product={product}
                    onAddToCart={addToCart}
                  />
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-12 bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 text-center animate-fade-in-up" style={{ animationDelay: `${(favorites.length + 3) * 100}ms` }}>
              <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                Ready to purchase your favorites?
              </h3>
              <p className="text-neutral-600 mb-6">
                Add all your favorite items to cart with one click
              </p>
              <button
                onClick={() => {
                  favorites.forEach(product => addToCart(product));
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-white rounded-xl font-semibold hover:bg-accent-700 hover:shadow-xl hover:shadow-accent-500/25 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v4a2 2 0 01-2 2H9a2 2 0 01-2-2v-4m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                </svg>
                Add All to Cart
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Favorites;